import { Button }                   from '@/components/ui/button';
import { Field }                    from '@/components/ui/field';
import CropperModal                 from '@/components/modal/CropperModal';
import Image                        from '@/components/Image/Image';
import CloseIcon                    from '@/assets/icons/CloseIcon';
import ContactModel                 from '@/models/services/network/ContactModel';
import FileModel                    from '@/models/services/file/FileModel';
import TypeModel                    from '@/models/services/file/TypeModel';
import { Box }                      from '@chakra-ui/react';
import { Grid }                     from '@chakra-ui/react';
import { GridItem }                 from '@chakra-ui/react';
import { Input }                    from '@chakra-ui/react';
import { NativeSelect }             from '@chakra-ui/react';
import { Stack }                    from '@chakra-ui/react';
import { Text }                     from '@chakra-ui/react';
import _capitalize                  from 'lodash/capitalize';
import React                        from 'react';
import { useForm }                  from 'react-hook-form';
import { useTranslation }           from 'react-i18next';
import { toaster }                  from '@/components/ui/toaster';
import useApiCollection             from '@widesk-core/hooks/useApiCollection';
import useFetch                     from '@widesk-core/hooks/useFetch';
import useFile                      from '@/hooks/useFile';

interface IEntityContactFormProps {
	contact?:        ContactModel;
	entityUrn?:      string;
	file?:           FileModel;
	onSuccess:       () => Promise<void>;
	onClose:         () => void;
}

interface FormValues {
	email:     string;
	firstname: string;
	gender:    string;
	lastname:  string;
}

const EntityContactForm = ({ contact, entityUrn, file, onSuccess, onClose }: IEntityContactFormProps) => {
	const { t } = useTranslation();

	const { source: fileSource } = useFile(file);

	const [selectedGender, setSelectedGender]         = React.useState(contact?.gender?.toString() || '1');
	const [nameUppercase, setNameUppercase]           = React.useState(contact?.lastname?.toUpperCase() || '');
	const [newProfilePicture, setNewProfilePicture]   = React.useState<File | undefined>();
	const [imageSource, setImageSource]               = React.useState<string>('');
	const [cropperImageSource, setCropperImageSource] = React.useState<string>('');
	const [cropperModalIsOpen, setCropperModalIsOpen] = React.useState(false);

	const typeCollection = useApiCollection(TypeModel);

	useFetch(async () => {
		await typeCollection.list();
	}, true);

	const { formState: { errors, isSubmitting }, handleSubmit, register, setValue } = useForm<FormValues>();

	React.useEffect(() => {
		if (contact?.id) {
			setValue('firstname', contact.firstname || '');
			setValue('lastname', contact.lastname || '');
			setValue('email', contact.email || '');
			setNameUppercase(contact.lastname?.toUpperCase() || '');
			setSelectedGender(contact.gender?.toString() || '1');
		}
	}, []);

	React.useEffect(() => {
		if (!imageSource) {
			if (contact?.photoUrl) {
				setImageSource(contact?.photoUrl)
			} else if (fileSource) {
				setImageSource(fileSource)
			}
		}
	}, [fileSource]);

	React.useEffect(() => {
		if (newProfilePicture) {
			setImageSource(URL.createObjectURL(newProfilePicture));
		}
	}, [newProfilePicture]);

	const isEditing = !!contact?.id;

	const onSubmit = handleSubmit(async (_data) => {
		try {
			const model = contact?.id ? contact : new ContactModel();
			await model.patch({
				firstname: _data.firstname,
				lastname:  _data.lastname,
				email:     _data.email,
				gender:    parseInt(selectedGender),
				...(entityUrn && !contact?.id ? { userUrn: entityUrn } : {}),
			});

			if (imageSource === '' && file) {
				await file.destroy();
			} else if (newProfilePicture instanceof File) {
				const newFile = new FileModel();
				const type    = typeCollection.find(m => m.reference === 'contact_photo') as TypeModel;
				await newFile.patchFormData({
					type:      type.urn,
					sourceUrn: model.urn,
					file:      newProfilePicture,
				});
			}

			toaster.create({
				title: t(isEditing ? 'pages.entity.contacts.modals.edit.messages.success' : 'pages.entity.contacts.modals.add.messages.success'),
				type:  'success',
			});
			await onSuccess();
		} catch {
			toaster.create({
				title: t(isEditing ? 'pages.entity.contacts.modals.edit.messages.error' : 'pages.entity.contacts.modals.add.messages.error'),
				type:  'error',
			});
		}
	});

	return (
		<form onSubmit={onSubmit}>
			<CropperModal
				imageSource={cropperImageSource}
				imageType={newProfilePicture?.type || ''}
				isOpen={cropperModalIsOpen}
				setIsOpen={setCropperModalIsOpen}
				onSubmit={async (croppedFile) => { setNewProfilePicture(croppedFile); }}
				onError={() => {
					toaster.create({
						title: t('pages.personalInformation.personalInformationBox.modals.cropper.messages.errors.error'),
						type: 'error',
					});
				}}
			/>

			<Grid templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(2, 1fr)' }}>
				<GridItem order={{ base: 1, lg: -1 }}>
					<Stack gap="20px" marginTop="20px">
						<Field
							label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.contacts.forms.contactForm.firstname')}</Text>}
							invalid={!!errors.firstname}
							required
						>
							<Input
								placeholder={t('pages.contacts.forms.contactForm.firstname')}
								{...register('firstname', { required: true })}
							/>
						</Field>

						<Field
							label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.contacts.forms.contactForm.lastname')}</Text>}
							invalid={!!errors.lastname}
							required
						>
							<Input
								value={nameUppercase}
								placeholder={t('pages.contacts.forms.contactForm.lastname')}
								{...register('lastname', { required: true })}
								onChange={e => setNameUppercase(e.target.value.toUpperCase())}
							/>
						</Field>

						<Field
							label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.contacts.forms.contactForm.email')}</Text>}
							invalid={!!errors.email}
							required
						>
							<Input
								placeholder={t('pages.contacts.forms.contactForm.email')}
								{...register('email', {
									required: true,
									pattern: {
										value: /^[a-z0-9]+([._-][a-z0-9]+)*@[a-z0-9]+([-][a-z0-9]+)*\.[a-z]{2,}$/,
										message: t('pages.contacts.forms.contactForm.errors.pattern'),
									},
								})}
							/>
							{errors.email?.type === 'pattern' && (
								<Text color="red">{t('pages.contacts.forms.contactForm.errors.pattern')}</Text>
							)}
						</Field>

						<Field
							label={<Text className="poppins-semibold" fontSize="14px" fontWeight="bold">{t('pages.contacts.forms.contactForm.gender')}</Text>}
							required
						>
							<NativeSelect.Root size="sm" width="240px">
								<NativeSelect.Field
									value={selectedGender}
									onChange={(e) => setSelectedGender(e.currentTarget.value)}
								>
									<option value="1">{_capitalize(t('app.selects.genders.man'))}</option>
									<option value="2">{_capitalize(t('app.selects.genders.woman'))}</option>
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Field>
					</Stack>
				</GridItem>

				<GridItem>
					<Box
						display="flex"
						justifyContent={{ lg: 'center' }}
						marginTop={{ lg: '20px' }}
						marginLeft={{ lg: '40px' }}
					>
						<Box display="flex" gap={2}>
							<Box width="200px" height="200px">
								<Image
									actions={{
										edit: (selectedFile: File) => {
											setCropperImageSource(URL.createObjectURL(selectedFile));
											setCropperModalIsOpen(true);
										},
										delete: () => {
											setNewProfilePicture(undefined);
											setImageSource('');
										},
									}}
									borderRadius="8px"
									errorSize="xl"
									src={imageSource}
									type="profile"
									height="200px"
									width="200px"
								/>
							</Box>
							<Box
								cursor="pointer"
								hideFrom="lg"
								hidden={!imageSource}
								onClick={() => {
									setNewProfilePicture(undefined);
									setImageSource('');
								}}
							>
								<CloseIcon size="lg" />
							</Box>
						</Box>
					</Box>
				</GridItem>
			</Grid>

			<Box display="flex" gap="10px" marginTop="30px">
				<Button variant="outline" onClick={onClose} flex="1">
					{t('app.actions.cancel')}
				</Button>
				<Button
					variant="solid"
					type="submit"
					loading={isSubmitting || typeCollection.isLoading}
					flex="1"
				>
					{t(isEditing ? 'pages.entity.contacts.actions.edit' : 'pages.entity.contacts.actions.add.label')}
				</Button>
			</Box>
		</form>
	);
};

export default EntityContactForm;
